import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";

const initial = { name: "", email: "", organization: "", message: "" };

export default function ContactSection() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Enquiry received. Our clinical team will reach out within 24 hours.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-surface py-24 md:py-32 border-t border-slate-200" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand mb-6">Partner with X-Gen</p>
          <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-[0.95] mb-8">
            Bring AI diagnostics
            <br />
            <span className="text-mute">to your institution.</span>
          </h2>
          <p className="text-mute text-base leading-relaxed max-w-md mb-10">
            Deployments at 40+ imaging centers. DICOM-native, PACS-integrated, live in under two weeks. Tell us about your workflow and we'll design a pilot.
          </p>
          <div className="space-y-3 font-mono text-xs uppercase tracking-[0.2em] text-mute">
            <p>hello@x-gen.ai</p>
            <p>HIPAA-aligned / SOC 2 Type II</p>
            <p>FDA 510(k) clearance pending</p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onSubmit={submit}
          className="md:col-span-7 border border-slate-200 bg-ink p-8 md:p-12 space-y-6 rounded-2xl shadow-[0_4px_20px_rgba(15,23,42,0.05)]"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="contact-name">Full name</label>
              <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Dr. Jane Carter" className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="contact-email">Work email</label>
              <Input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="j.carter@hospital.org" className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
            </div>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="contact-org">Institution / organization</label>
            <Input id="contact-org" data-testid="contact-org-input" value={form.organization} onChange={set("organization")} placeholder="Metro General Imaging Center" className="bg-surface border-slate-200 rounded-lg h-12 focus-visible:ring-brand" />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute block mb-2" htmlFor="contact-message">Message</label>
            <Textarea id="contact-message" data-testid="contact-message-input" value={form.message} onChange={set("message")} placeholder="Tell us about your imaging volume, modalities and current workflow..." rows={5} className="bg-surface border-slate-200 rounded-lg focus-visible:ring-brand resize-none" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-button"
            className="flex items-center gap-2 bg-brand text-white font-mono text-xs uppercase tracking-[0.15em] px-8 py-4 hover:bg-[#0F172A] hover:text-white transition-colors duration-300 disabled:opacity-50 disabled:pointer-events-none"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUpRight className="w-4 h-4" />}
            {sending ? "Sending..." : "Request a pilot"}
          </button>
        </motion.form>
      </div>
    </section>
  );
}
